import { useState, useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/shows';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/category/all?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  }, [searchQuery, navigate]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-16" aria-label="主导航">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
            6789<span className="text-foreground-900">影院</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
              location.pathname === '/' ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'
            }`}
          >
            首页
          </Link>
          {categories.filter(c => c.slug !== 'all').slice(0, 5).map(cat => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === `/category/${cat.slug}` ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              {cat.name}
            </Link>
          ))}
          <Link
            to="/about"
            className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
              location.pathname === '/about' ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500'
            }`}
          >
            关于我们
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索综艺节目..."
              className="w-48 lg:w-56 h-9 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
            />
            <button type="submit" className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer" aria-label="搜索">
              <i className="ri-search-line text-sm"></i>
            </button>
          </form>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer"
          aria-label="菜单"
          aria-expanded={isMobileMenuOpen}
        >
          <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
        </button>
      </nav>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg animate-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-4 space-y-3">
            <form onSubmit={handleSearch} className="relative mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索综艺节目..."
                className="w-full h-10 pl-10 pr-4 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
              <button type="submit" className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 cursor-pointer">
                <i className="ri-search-line text-sm"></i>
              </button>
            </form>
            <Link to="/" className="block py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors">首页</Link>
            {categories.filter(c => c.slug !== 'all').map(cat => (
              <Link key={cat.slug} to={`/category/${cat.slug}`} className="block py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors">{cat.name}</Link>
            ))}
            <Link to="/about" className="block py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors">关于我们</Link>
          </div>
        </div>
      )}
    </header>
  );
}